// Full-screen overlay loader shown while POST /v1/flights/search/ is in
// flight. Markup mirrors 13-searching.html (loader-overlay > loader-card)
// with the SVG route + plane animation, Kaira mark, and rotating status
// text. For round-trip searches we draw a second curved path (and a second
// plane) so the user sees both legs animating.

import { lookupAirport } from "../lib/airports";
import { KairaMark } from "./KairaMark";

interface Props {
  origin: string;
  destination: string;
  isRoundTrip?: boolean;
  /** "Mon, 15 Jun" */
  depart: string;
  /** "Sat, 20 Jun"  — RT only */
  return_?: string;
  paxLabel: string;
  cabinLabel: string;
}

export function SearchingLoader({ origin, destination, isRoundTrip, depart, return_, paxLabel, cabinLabel }: Props) {
  const from = lookupAirport(origin);
  const to = lookupAirport(destination);
  return (
    <div className="loader-overlay">
      <div className="loader-card">
        <div className="search-loader-stage" style={{ height: isRoundTrip ? 180 : 140 }}>
          <svg className="search-loader-svg" viewBox={`0 0 400 ${isRoundTrip ? 180 : 140}`} preserveAspectRatio="xMidYMid meet">
            {/* Outbound curve — top arc going right */}
            <path className="route-path" d="M 60 95 Q 200 25 340 95" />
            <path className="route-path-fg" d="M 60 95 Q 200 25 340 95" />

            {/* Outbound pin (origin) */}
            <circle className="route-pin" cx={60} cy={95} r={8} />
            <circle cx={60} cy={95} r={3} fill="white" />
            <text className="route-label" x={60} y={120} textAnchor="middle">{from.code}</text>
            <text className="route-label-sub" x={60} y={134} textAnchor="middle">{from.city}</text>

            {/* Destination pin */}
            <circle className="route-pin dest" cx={340} cy={95} r={8} />
            <text className="route-label" x={340} y={120} textAnchor="middle">{to.code}</text>
            <text className="route-label-sub" x={340} y={134} textAnchor="middle">{to.city}</text>

            {/* Outbound plane */}
            <g className="route-plane">
              <path
                d="M -8 0 L 8 0 M 4 -3 L 8 0 L 4 3 M -2 -4 L 2 -4 L 0 -8 Z M -2 4 L 2 4 L 0 8 Z"
                fill="var(--ink-rail)" stroke="var(--ink-rail)" strokeWidth={1.5}
                strokeLinejoin="round" strokeLinecap="round"
              />
            </g>

            {/* Round-trip: second curve going LEFT (return) — bottom arc */}
            {isRoundTrip && (
              <>
                <path className="route-path return" d="M 340 105 Q 200 175 60 105" />
                <path className="route-path-fg return" d="M 340 105 Q 200 175 60 105" />
                <g className="route-plane return-plane">
                  <path
                    d="M -8 0 L 8 0 M 4 -3 L 8 0 L 4 3 M -2 -4 L 2 -4 L 0 -8 Z M -2 4 L 2 4 L 0 8 Z"
                    fill="var(--ink-rail)" stroke="var(--ink-rail)" strokeWidth={1.5}
                    strokeLinejoin="round" strokeLinecap="round"
                    transform="rotate(180)"
                  />
                </g>
              </>
            )}
          </svg>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div className="loader-kaira" style={{ background: "transparent", boxShadow: "none", border: "none" }}>
            <KairaMark size={36} />
          </div>
        </div>

        <div className="status-stage">
          <span className="status-text">Reaching out to <span className="serif">airlines</span>...</span>
          <span className="status-text">Comparing fares across carriers</span>
          <span className="status-text">Sorting by what'll <span className="serif">work for you</span>...</span>
          <span className="status-text">Almost there...</span>
        </div>

        <div className="stage-dots">
          <span className="sd" /><span className="sd" /><span className="sd" /><span className="sd" />
        </div>

        {/* Search context — one line per dimension so the loader does double
            duty as confirmation of what we're actually searching for. */}
        <div className="search-context">
          <span className="row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
            <span>
              {depart}
              {isRoundTrip && return_ && <> &nbsp;<span style={{ color: "var(--ink-4)" }}>→</span>&nbsp; {return_}</>}
            </span>
          </span>
          <span className="dot-sep">·</span>
          <span className="row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            <span>{paxLabel}</span>
          </span>
          <span className="dot-sep">·</span>
          <span className="row">{cabinLabel}</span>
        </div>

        <p style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 12 }}>
          This usually takes 2–5 seconds.
        </p>
      </div>
    </div>
  );
}
